import _ from 'lodash'
import store from '@/store'

export const DEFAULT_USERNAME = 'annon'

function username() {
  return store?.getters['account/getUserName'] || DEFAULT_USERNAME
}

function kRoutes(user = username()) {
  return `nyssma.settings.routes.${user}`
}
function kSettings(routeKey, user = username()) {
  return `nyssma.settings.${routeKey}.${user}`
}

function safeParse(json, fallback) {
  try {
    return JSON.parse(json)
  } catch {
    return fallback
  }
}

export default {
  namespaced: true,

  state: () => ({
    // reactive copies of what’s in localStorage
    routes: [],
    items: {},
    hydratedUser: null,
  }),

  actions: {
    /** Call this once on app boot, and again after login if username changes */
    hydrate({ commit }, user) {
      commit('hydrate', user)
    },

    updateItem({ commit }, payload) {
      commit('updateItem', payload)
    },
    removeItem({ commit }, payload) {
      commit('removeItem', payload)
    },

    updateRoute({ commit }, payload) {
      commit('updateRoute', payload)
    },
    updateRoutes({ commit }, payload) {
      commit('updateRoutes', payload)
    },

    /** Optional: call this when user logs out */
    clearAll({ commit }) {
      commit('clearAll')
    },
  },

  mutations: {
    hydrate(state, user) {
      const u = user || username()
      state.hydratedUser = u

      // load routes
      state.routes = safeParse(localStorage.getItem(kRoutes(u)) || '[]', [])

      // load per-route settings into state.items
      const prefix = 'nyssma.settings.'
      const suffix = `.${u}`

      const items = Object.keys(localStorage)
          .filter((key) => key.startsWith(prefix) && key.endsWith(suffix) && !key.startsWith('nyssma.settings.routes.'))
          .reduce((obj, key) => {
            // key is nyssma.settings.<routeKey>.<user>
            const parts = key.split('.')
            // ["nyssma","settings","<routeKey>", "<user>"]
            const routeKey = parts[2]
            obj[routeKey] = safeParse(localStorage.getItem(key) || 'null', null)
            return obj
          }, {})

      state.items = items
    },

    /** Writes route settings object (or nested subKey) */
    updateItem(state, payload) {
      const u = username()
      const route = state.routes.find(r => r?.name === payload.key)

      // preserve your old behavior: only persist if route is enabled
      if (!route?.enabled) return

      const routeKey = payload.key
      const storageKey = kSettings(routeKey, u)

      const current = state.items[routeKey] ?? {}

      if (!payload.subKey) {
        state.items = { ...state.items, [routeKey]: payload.value }
        localStorage.setItem(storageKey, JSON.stringify(payload.value))
        return
      }

      const next = _.cloneDeep(current)
      _.set(next, payload.subKey, payload.value)

      state.items = { ...state.items, [routeKey]: next }
      localStorage.setItem(storageKey, JSON.stringify(next))
    },

    removeItem(state, payload) {
      const u = username()
      const routeKey = payload.key
      localStorage.removeItem(kSettings(routeKey, u))

      const next = { ...state.items }
      delete next[routeKey]
      state.items = next
    },

    /** Replace ALL routes list */
    updateRoutes(state, payload) {
      const u = username()
      state.routes = Array.isArray(payload.value) ? payload.value : []
      localStorage.setItem(kRoutes(u), JSON.stringify(state.routes))
    },

    /**
     * Patch ONE route (reactive + persisted)
     * Contract: payload = { key: 'route-name', value: { isLocalEnabled: true } }
     */
    updateRoute(state, payload) {
      const u = username()
      const routes = Array.isArray(state.routes) ? [...state.routes] : []

      const i = routes.findIndex(r => r?.name === payload.key)
      if (i >= 0) {
        routes[i] = { ...routes[i], ...payload.value }
      } else {
        routes.push({ name: payload.key, ...payload.value })
      }

      state.routes = routes
      localStorage.setItem(kRoutes(u), JSON.stringify(routes))
    },

    clearAll(state) {
      const u = username()

      // remove all nyssma.settings.*.<user> keys
      for (const key of Object.keys(localStorage)) {
        if (key.startsWith('nyssma.settings') && key.endsWith(`.${u}`)) {
          localStorage.removeItem(key)
        }
      }

      state.routes = []
      state.items = {}
      state.hydratedUser = u
    },
  },

  getters: {
    // settings objects
    getItem: (state) => (key) => state.items?.[key] ?? null,
    getSubItem: (state) => (key, path, defaultValue) => _.get(state.items?.[key], path, defaultValue),

    // routes
    getRoute: (state) => (name) => state.routes?.find(r => r?.name === name) ?? null,
    getRoutes: (state) => state.routes ?? [],

    isRouteDisabled: (state) => (key) => {
      const route = state.routes?.find(r => r?.name === key)
      if (!route) return true
      return route.enabled === false || route.isLocalEnabled === false
    },
    isRouteEnabled: (state) => (key) => {
      const route = state.routes?.find(r => r?.name === key)
      return !!route?.enabled
    },
    isRouteLocalEnabled: (state) => (key) => {
      const route = state.routes?.find(r => r?.name === key)
      return !!(route?.enabled && route?.isLocalEnabled)
    },
    isRouteCacheEnabled: (state) => (key) => {
      const route = state.routes?.find(r => r?.name === key)
      return !!(route?.enabled && route?.isCacheEnabled)
    },
  },
}
